import React, { useState, useEffect } from 'react';
import './SecretsModal.css';
import { modalInputClass } from './modalInputClass';
import { useModalSelectionGuard, preventModalOverlaySelection } from '../hooks/useModalSelectionGuard';

const BrokerCloseModal = ({
  isOpen,
  onClose,
  onSubmit,
  deal,
  openDealsForFutures = []
}) => {
  useModalSelectionGuard(isOpen);

  const totalFuturesCount = openDealsForFutures.reduce((sum, d) => sum + (d.futuresCount || 1), 0);
  const [futuresCountStr, setFuturesCountStr] = useState(String(totalFuturesCount));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && deal) {
      setFuturesCountStr(totalFuturesCount > 0 ? String(totalFuturesCount) : '');
      setError(null);
    }
  }, [isOpen, deal, totalFuturesCount]);

  const handleFuturesCountChange = (e) => {
    const value = e.target.value.replace(/[^\d]/g, '');
    setFuturesCountStr(value);
    if (error) setError(null);
  };

  const handleCancel = () => {
    setFuturesCountStr('');
    setError(null);
    onClose();
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleCancel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleCancel]);

  const handleOk = () => {
    const futuresCountValue = parseInt(futuresCountStr, 10);

    if (isNaN(futuresCountValue) || futuresCountValue < 1) {
      setError('Введите корректное количество фьючерсов (не менее 1)');
      return;
    }
    if (futuresCountValue > totalFuturesCount) {
      setError(`Максимум доступно: ${totalFuturesCount} фьючерсов`);
      return;
    }

    onSubmit({
      broker: deal.broker,
      stock: deal.stock,
      futures: deal.futures,
      futuresCount: futuresCountValue
    });
  };

  if (!isOpen || !deal) return null;

  const brokerLabel = deal.broker === 'bcs' ? 'БКС' : deal.broker === 'finam' ? 'Финам' : deal.broker;
  const defaultFuturesCountValue = totalFuturesCount > 0 ? String(totalFuturesCount) : '';

  return (
    <div className="secrets-modal-overlay" onSelectStart={preventModalOverlaySelection}>
      <div className="secrets-modal" onClick={e => e.stopPropagation()}>
        <div className="secrets-modal-header">
          <h2>Закрыть у брокера разово</h2>
          <button className="secrets-close-btn" onClick={handleCancel}>×</button>
        </div>

        <div className="secrets-form">
          <div className="form-group">
            <div className="secrets-modal-summary">
              {deal.stock} / {deal.futures} · {brokerLabel}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="brokerCloseFuturesCount">Количество фьючерсов к закрытию</label>
            <div className="secrets-input-with-clear">
              <input
                type="text"
                id="brokerCloseFuturesCount"
                value={futuresCountStr}
                onChange={handleFuturesCountChange}
                placeholder={`Макс. ${totalFuturesCount}`}
                inputMode="numeric"
                className={modalInputClass({ withActions: true, error: Boolean(error) })}
              />
              <div className="secrets-input-actions">
                <button
                  type="button"
                  onClick={() => {
                    setFuturesCountStr(defaultFuturesCountValue);
                    if (error) setError(null);
                  }}
                  className="secrets-input-action-btn"
                  aria-label="Восстановить количество фьючерсов по умолчанию"
                  disabled={futuresCountStr === defaultFuturesCountValue}
                >
                  ↺
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setFuturesCountStr('');
                    if (error) setError(null);
                  }}
                  className="secrets-input-action-btn"
                  aria-label="Очистить количество фьючерсов"
                  disabled={!futuresCountStr}
                >
                  ×
                </button>
              </div>
            </div>
          </div>

          <div className="form-group">
            <p className="secrets-modal-hint">
              Будет выполнена реальная продажа у брокера по рыночной цене.
            </p>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="secrets-modal-buttons">
            <button type="button" className="secrets-cancel-btn" onClick={handleCancel}>
              Отмена
            </button>
            <button type="button" className="secrets-save-btn" onClick={handleOk}>
              Закрыть у брокера разово
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrokerCloseModal;
